import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { previewInvite, acceptInvite, publicInvitePreview, joinViaPublicInvite, errorMessage, getToken } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Loader2, Users, LogIn, UserPlus, ArrowRight } from "lucide-react";
import { MyAELogo } from "../components/myae/Logo";

export default function GroupJoin() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, setAuthOpen } = useAuth();
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [joining, setJoining] = useState(false);

  useEffect(() => {
    // Stash token so AuthModal / register flow can pick it up after sign-in
    try { localStorage.setItem("myae_pending_group_invite", token); } catch (e) { /* ignore */ }
    // Try the public reusable invite first (new AI-first flow). If it 404s,
    // fall back to the single-use email invite (legacy flow).
    publicInvitePreview(token)
      .then((p) => setPreview({ ...p, kind: "public" }))
      .catch(() => {
        previewInvite(token)
          .then((p) => setPreview({ ...p, kind: "single" }))
          .catch((e) => setError(errorMessage(e, "This invite is invalid or expired.")));
      });
  }, [token]);

  useEffect(() => {
    // If a user is already signed in when we land on the page (or immediately
    // after they sign in), attempt to join.
    if (!user || !preview) return;
    const doJoin = async () => {
      setJoining(true);
      try {
        const res = preview.kind === "public"
          ? await joinViaPublicInvite(token)
          : await acceptInvite(token);
        try { localStorage.removeItem("myae_pending_group_invite"); } catch (e) { /* ignore */ }
        toast.success(res.already_joined ? "You're already in this group." : "Joined the group!");
        navigate(`/groups/${res.group_id}`);
      } catch (e) {
        setError(errorMessage(e, "Could not join this group."));
      } finally {
        setJoining(false);
      }
    };
    doJoin();
  }, [user, preview, token, navigate]);

  const openSignIn = (mode = "login") => {
    setAuthOpen({ open: true, mode });
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 p-6">
        <div className="max-w-md w-full text-center rounded-2xl bg-white border border-zinc-200 p-8">
          <MyAELogo size={40} className="mx-auto mb-4" />
          <p className="font-display text-2xl text-zinc-900 mb-2">Invite unavailable</p>
          <p className="text-sm text-zinc-600">{error}</p>
          <button onClick={() => navigate("/")} className="mt-6 text-sm text-indigo-600">Go to MyAE home</button>
        </div>
      </div>
    );
  }

  if (!preview) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <Loader2 size={22} className="text-zinc-400 animate-spin" />
      </div>
    );
  }

  const needsRegistration = preview.kind === "single" && preview.invitee_status === "pending_register" && !user;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-indigo-50 to-zinc-50 p-6" data-testid="group-join-page">
      <div className="max-w-lg w-full rounded-3xl bg-white border border-zinc-200 shadow-sm p-8 text-center">
        <MyAELogo size={38} className="mx-auto mb-5" />
        <div className="rounded-full bg-indigo-50 p-3 w-fit mx-auto mb-4">
          <Users size={26} className="text-indigo-600" />
        </div>
        <p className="text-xs uppercase tracking-wide font-semibold text-indigo-600 mb-1">Group assignment invite</p>
        <h1 className="font-display text-3xl text-zinc-900 mb-2">You&rsquo;ve been invited to collaborate.</h1>
        <p className="text-sm text-zinc-600 mb-5">
          <span className="font-medium text-zinc-900">{preview.inviter_name}</span> has invited you to contribute a section of:
        </p>
        <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-4 mb-6 text-left">
          <p className="font-display text-lg text-zinc-900">{preview.title}</p>
          {preview.module && <p className="text-xs text-zinc-500 mt-1">{preview.module}</p>}
          {preview.description && <p className="text-xs text-zinc-600 mt-2">{preview.description}</p>}
        </div>

        {joining ? (
          <div className="inline-flex items-center gap-2 text-sm text-zinc-600">
            <Loader2 size={16} className="animate-spin" /> Joining group…
          </div>
        ) : user ? (
          <p className="text-sm text-zinc-500">Redirecting you to the group…</p>
        ) : needsRegistration ? (
          <div className="space-y-3">
            <p className="text-sm text-zinc-600">
              You don&rsquo;t have a MyAE account yet. Create one (it&rsquo;s free — takes 30 seconds) and we&rsquo;ll add you to the group automatically.
            </p>
            <button
              onClick={() => openSignIn("register")}
              data-testid="join-signup-btn"
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 text-white px-5 py-3 text-sm font-semibold hover:bg-indigo-700"
            >
              <UserPlus size={16} /> Create free account & join
            </button>
            <button
              onClick={() => openSignIn("login")}
              data-testid="join-signin-btn"
              className="w-full text-sm text-zinc-600 hover:text-zinc-900 py-2"
            >
              Already have a MyAE account? Sign in
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <button
              onClick={() => openSignIn("login")}
              data-testid="join-signin-primary-btn"
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 text-white px-5 py-3 text-sm font-semibold hover:bg-indigo-700"
            >
              <LogIn size={16} /> Sign in to join
            </button>
            <button
              onClick={() => openSignIn("register")}
              data-testid="join-signup-btn"
              className="w-full text-sm text-zinc-600 hover:text-zinc-900 py-2"
            >
              Don&rsquo;t have an account? Sign up <ArrowRight size={12} className="inline" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
